// Client-side tracking. Fires events to:
//   1. Meta Pixel (window.fbq)
//   2. GA4 (window.gtag)
//   3. Microsoft Clarity (window.clarity, automatic — no manual events)
//   4. Our /api/events/web Worker (server-side mirror — Supabase + Meta CAPI + Google MP)
//
// Pixel/GA4/Clarity tags are loaded via <script> in public/index.html. They
// no-op gracefully if their IDs are placeholder/missing (fbq becomes a queue
// stub, gtag is a function that pushes to dataLayer).
//
// All event firing is non-blocking. We use sendBeacon for the Worker call so
// the navigation isn't delayed when a user clicks a buy button (the buy_click
// event must reach the Worker BEFORE the browser leaves our page for Amazon).

import { v4 as uuidv4 } from 'uuid';

// ─── Visitor + session IDs ──────────────────────────────────────────

const VISITOR_KEY = 'dw_visitor_id';
const SESSION_KEY = 'dw_session_id';
const SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30 min

export function getVisitorId() {
  let id = safeGetLocalStorage(VISITOR_KEY);
  if (!id) {
    id = uuidv4();
    safeSetLocalStorage(VISITOR_KEY, id);
  }
  return id;
}

export function getSessionId() {
  const raw = safeGetLocalStorage(SESSION_KEY);
  const now = Date.now();
  if (raw) {
    try {
      const { id, last } = JSON.parse(raw);
      if (now - last < SESSION_TIMEOUT_MS) {
        // Roll the timeout
        safeSetLocalStorage(SESSION_KEY, JSON.stringify({ id, last: now }));
        return id;
      }
    } catch {}
  }
  const id = uuidv4();
  safeSetLocalStorage(SESSION_KEY, JSON.stringify({ id, last: now }));
  return id;
}

// ─── Click ID + UTM capture ──────────────────────────────────────────

const CLICK_ID_KEY = 'dw_click_ids';
const UTM_KEY = 'dw_utm';
const COOKIE_DAYS = 90;

const CLICK_PARAMS = ['fbclid', 'gclid', 'gbraid', 'wbraid', 'msclkid', 'ttclid', 'epik'];
const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];

export function captureClickIdsAndUTM() {
  if (typeof window === 'undefined') return { clickIds: {}, utm: {} };
  const url = new URL(window.location.href);

  // Click IDs
  const existingClickIds = safeGetLocalStorage(CLICK_ID_KEY);
  const clickIds = existingClickIds ? safeJSONParse(existingClickIds) : {};
  let clickIdsUpdated = false;
  for (const p of CLICK_PARAMS) {
    const v = url.searchParams.get(p);
    if (v) { clickIds[p] = v; clickIdsUpdated = true; }
  }
  if (clickIdsUpdated) {
    safeSetLocalStorage(CLICK_ID_KEY, JSON.stringify(clickIds));
    setCookie('_dw_click_ids', JSON.stringify(clickIds), COOKIE_DAYS);
  }

  // Special: write Meta's _fbc cookie when fbclid is fresh (Meta reads this)
  if (url.searchParams.get('fbclid')) {
    const fbc = `fb.1.${Date.now()}.${url.searchParams.get('fbclid')}`;
    setCookie('_fbc', fbc, COOKIE_DAYS);
  }

  // UTM
  const existingUTM = safeGetLocalStorage(UTM_KEY);
  const utm = existingUTM ? safeJSONParse(existingUTM) : {};
  let utmUpdated = false;
  for (const p of UTM_PARAMS) {
    const v = url.searchParams.get(p);
    if (v) { utm[p] = v; utmUpdated = true; }
  }
  if (utmUpdated) {
    safeSetLocalStorage(UTM_KEY, JSON.stringify(utm));
  }

  return { clickIds, utm };
}

export function getStoredClickIds() {
  return safeJSONParse(safeGetLocalStorage(CLICK_ID_KEY) || '{}');
}

export function getStoredUTM() {
  return safeJSONParse(safeGetLocalStorage(UTM_KEY) || '{}');
}

// ─── Consent ─────────────────────────────────────────────────────────

const CONSENT_KEY = 'dw_consent';

export function getConsent() {
  const raw = safeGetLocalStorage(CONSENT_KEY);
  if (!raw) return { analytics: false, marketing: false, decided: false };
  return { ...safeJSONParse(raw), decided: true };
}

export function setConsent(analytics, marketing) {
  safeSetLocalStorage(CONSENT_KEY, JSON.stringify({ analytics, marketing }));
  // Update Meta + GA4 consent state
  if (window.fbq) {
    window.fbq('consent', marketing ? 'grant' : 'revoke');
  }
  if (window.gtag) {
    window.gtag('consent', 'update', {
      ad_storage: marketing ? 'granted' : 'denied',
      analytics_storage: analytics ? 'granted' : 'denied',
      ad_user_data: marketing ? 'granted' : 'denied',
      ad_personalization: marketing ? 'granted' : 'denied',
    });
  }
}

// ─── Core event-fire ─────────────────────────────────────────────────

const WORKER_ENDPOINT = '/api/events/web';

// Non-standard event names get sent to the Worker only (Meta/GA4 still receive
// them as custom events, but only `view_content`, `buy_click`, `email_signup`
// map to Meta's standard catalog on the server side).
export function trackEvent(eventName, properties = {}) {
  if (typeof window === 'undefined') return;
  const visitor_id = getVisitorId();
  const session_id = getSessionId();
  const event_id = uuidv4();
  const consent = getConsent();
  const clickIds = getStoredClickIds();
  const utm = getStoredUTM();

  // 1. Meta Pixel
  if (consent.marketing && window.fbq) {
    const metaName = mapToMetaStandard(eventName);
    if (metaName) {
      window.fbq('track', metaName, properties, { eventID: event_id });
    } else {
      window.fbq('trackCustom', eventName, properties, { eventID: event_id });
    }
  }

  // 2. GA4 — mirror with event_id so Kabir can join GA4 ↔ Pixel ↔ Postgres
  // rows in cross-pipe diagnostics. GA4 doesn't dedup on event_id (Meta does);
  // we just want it in the payload as a correlation key.
  // `book_slug` aliased from `title_id` per Kabir's tag spec.
  if (consent.analytics && window.gtag) {
    const ga4Params = {
      ...properties,
      event_id,
      ...(properties.title_id && { book_slug: properties.title_id }),
    };
    window.gtag('event', eventName.replace(/[^a-zA-Z0-9_]/g, '_').slice(0, 40), ga4Params);
  }

  // 3. Worker (server-side mirror — first-party, runs even without consent for
  //    analytics-only writes; consent gates Meta CAPI + Google MP forwarding inside the Worker)
  const payload = {
    visitor_id,
    session_id,
    event_id,
    event_name: eventName,
    page_url: window.location.href,
    page_slug: window.location.pathname,
    title_id: properties.title_id,
    store: properties.store,
    market: properties.market,
    fbclid: clickIds.fbclid,
    gclid: clickIds.gclid,
    gbraid: clickIds.gbraid,
    wbraid: clickIds.wbraid,
    msclkid: clickIds.msclkid,
    ttclid: clickIds.ttclid,
    utm_source: utm.utm_source,
    utm_medium: utm.utm_medium,
    utm_campaign: utm.utm_campaign,
    utm_content: utm.utm_content,
    utm_term: utm.utm_term,
    custom_data: properties,
    consent: { analytics: consent.analytics, marketing: consent.marketing },
    fbc: getCookie('_fbc'),
    fbp: getCookie('_fbp'),
    ga_client_id: parseGaClientId(getCookie('_ga')),  // for MP client_id parity with on-page gtag
  };

  // sendBeacon survives navigation — critical for buy_click → outbound link
  const sent = navigator.sendBeacon
    ? navigator.sendBeacon(WORKER_ENDPOINT, new Blob([JSON.stringify(payload)], { type: 'application/json' }))
    : false;
  if (!sent) {
    // Fallback: fire-and-forget fetch
    fetch(WORKER_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {});
  }
}

function mapToMetaStandard(name) {
  switch (name) {
    case 'view_content':       return 'ViewContent';
    case 'buy_click':          return 'AddToCart';
    case 'email_signup':       return 'Lead';
    default:                   return null;
  }
}

// ─── Buy-click navigation outcome diagnostic ─────────────────────────
//
// FB's in-app browser silently blocks outbound nav to Amazon / Play
// Store on Android. The buy_click event fires correctly but the user
// never actually leaves our page. We can't see the failure server-side
// without surfacing it explicitly — so 3s after a buy_click, if the
// page is still visible AND the URL is unchanged AND no `pagehide`
// event fired, we report `buy_click_blocked`. Caller of trackEvent
// for buy_click invokes this immediately afterward.
export function reportBuyNavOutcome(properties = {}) {
  if (typeof window === 'undefined') return;
  const startUrl = window.location.href;
  let navigated = false;
  const onHide = () => { navigated = true; };
  // pagehide fires when the document is being unloaded for navigation
  // (the success case) — also when bfcache evicts, but that's rare in
  // a fresh-tap scenario.
  window.addEventListener('pagehide', onHide, { once: true });

  setTimeout(() => {
    window.removeEventListener('pagehide', onHide);
    if (navigated) return;
    if (typeof document === 'undefined') return;
    if (document.visibilityState !== 'visible') return;       // app backgrounded — assume it took
    if (window.location.href !== startUrl) return;            // nav happened (SPA route etc.)
    trackEvent('buy_click_blocked', {
      ...properties,
      ua: (navigator.userAgent || '').slice(0, 200),
      in_app_browser: /FB_IAB|FBAN|FBAV|FBIOS|Instagram|Messenger/i.test(navigator.userAgent || ''),
      platform: /Android/i.test(navigator.userAgent || '') ? 'android'
              : /iPhone|iPad|iPod/i.test(navigator.userAgent || '') ? 'ios' : 'other',
    });
  }, 3000);
}

// ─── Engagement: scroll depth + dwell time ───────────────────────────

const FIRED_KEY = '__dw_fired_';

function fireOnce(key, fn) {
  if (window[FIRED_KEY + key]) return;
  window[FIRED_KEY + key] = true;
  fn();
}

export function installEngagementTracking({ title_id, slug }) {
  if (typeof window === 'undefined') return () => {};

  const onScroll = () => {
    const scrollTop = window.scrollY;
    const docHeight = Math.max(
      document.body.scrollHeight - window.innerHeight,
      1
    );
    const pct = Math.round((scrollTop / docHeight) * 100);
    [25, 50, 75, 90].forEach((threshold) => {
      if (pct >= threshold) {
        fireOnce(`scroll_${threshold}_${slug}`, () =>
          trackEvent(`eng_scroll_${threshold}`, { title_id, slug, depth_pct: threshold })
        );
      }
    });
  };

  const startedAt = Date.now();
  const timers = [];
  [10, 30, 60, 120].forEach((seconds) => {
    timers.push(
      setTimeout(() => {
        if (document.visibilityState === 'visible') {
          fireOnce(`time_${seconds}_${slug}`, () =>
            trackEvent(`eng_time_${seconds}s`, { title_id, slug, seconds })
          );
        }
      }, seconds * 1000)
    );
  });

  window.addEventListener('scroll', onScroll, { passive: true });
  return () => {
    window.removeEventListener('scroll', onScroll);
    timers.forEach(clearTimeout);
  };
}

// ─── Storage + cookie helpers ────────────────────────────────────────

function safeGetLocalStorage(k) {
  try { return window.localStorage.getItem(k); } catch { return null; }
}
function safeSetLocalStorage(k, v) {
  try { window.localStorage.setItem(k, v); } catch {}
}
function safeJSONParse(s) {
  try { return JSON.parse(s); } catch { return {}; }
}
function setCookie(name, value, days) {
  try {
    const d = new Date();
    d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${d.toUTCString()}; path=/; SameSite=Lax; Secure`;
  } catch {}
}
function getCookie(name) {
  if (typeof document === 'undefined') return null;
  const m = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return m ? decodeURIComponent(m[2]) : null;
}

// Extract the GA4 client_id from the `_ga` cookie. Format is `GA1.1.<cid>.<ts>`.
// Returns the `<cid>.<ts>` segment which is what GA4 uses as client_id (and what
// the gtag.js library uses on the browser side).
function parseGaClientId(raw) {
  if (!raw) return null;
  const parts = raw.split('.');
  // GA1.1.123456789.1690000000  →  123456789.1690000000
  return parts.length >= 4 ? `${parts[2]}.${parts[3]}` : null;
}
